@if (showGhostElement) {
  <div class="mt-4">
    <div
      class="skeleton-box-card"
      role="status"
      aria-live="polite"
      [attr.aria-label]="'Loading stores'"
    >
      <div role="list" [attr.aria-label]="'Loading store options'">
        <div class="row">
          @for (ghostElement of slotGhostElements; track $index) {
            <div class="col-md-6" role="listitem">
              <div
                class="d-flex stats-card shadow"
                role="presentation"
                [attr.aria-hidden]="true"
              >
                <div class="skeleton-animation w-100 d-flex justify-content-center flex-column gap-3">
                  <div class="bar bar-1"></div>
                  <div class="bar bar-2"></div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
} @else {
  <form #storeForm="ngForm" role="form" [attr.aria-label]="'Store selection'">
    <!-- Store Card View Starts -->
    @if (
      stores?.length &&
      enableStorePostcodeFeature !== 1 &&
      widgetService?.widgetConf?.partner?.show_stores_as_dropdown !== 1
    ) {
      <div
        class="row cbsw-store-selection-step"
        role="region"
        [attr.aria-label]="'Store selection'"
      >
        <div class="col-md-12">
          <div>
            <app-csw-text
              customClass="step-element-title fw-600"
              [text]="storeLabelTemplate | translation: 'template_content': lang | resolvePlaceholder: { store: bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore } | calioSafe: 'html'"
              role="heading"
              aria-level="2"
            />
          </div>
        </div>

        @if (stores?.length > 1 && bookingService?.partnerData?.widget_show_all_stores_card === 1) {
          <div class="mt-3 col-md-6 cursor-pointer">
            <app-info-card
              [showCardBorder]="true"
              [showShadow]="false"
              [showBody]="true"
              [selectedCardColor]="widgetService?.widgetConf?.header_background_color"
              [isSelected]="selectedStoreId === 0"
              role="button"
              [attr.aria-label]="(anyStoreLabelTemplate | translation: 'template_content': lang) + (selectedStoreId === 0 ? ' - Selected' : ' - Not selected')"
              [attr.aria-selected]="selectedStoreId === 0"
              [attr.aria-pressed]="selectedStoreId === 0"
              tabindex="0"
            >
              <div
                cardBody
                (click)="onStoreChange(0)"
                role="region"
                [attr.aria-label]="'Any store option'"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <app-csw-text
                      [text]="anyStoreLabelTemplate | translation: 'template_content': lang | calioSafe: 'html'"
                      customClass="fw-600 fw-13"
                      role="heading"
                      aria-level="3"
                    />
                  </div>
                  <div class="next-arrow-container">
                    @if (selectedStoreId === 0) {
                      <span
                        (click)="next()"
                        role="button"
                        [attr.aria-label]="'Continue with any store'"
                        tabindex="0"
                      >
                        <img
                          [src]="baseUrl + '/images/next_icon_widget.svg'"
                          width="26px"
                          alt="Continue"
                        >
                      </span>
                    }
                  </div>
                </div>
              </div>
            </app-info-card>
          </div>
        }

        @for (store of stores; track store) {
          <div class="mt-3 col-md-6 cursor-pointer" role="listitem">
            <app-info-card
              [showCardBorder]="true"
              [showShadow]="false"
              [showBody]="true"
              [selectedCardColor]="widgetService?.widgetConf?.header_background_color"
              [isSelected]="selectedStoreId === store.id"
              [isDisabled]="store.disabled"
              role="button"
              [attr.aria-label]="(store | translation: 'name': lang) + (selectedStoreId === store.id ? ' - Selected' : ' - Not selected')"
              [attr.aria-selected]="selectedStoreId === store.id"
              [attr.aria-pressed]="selectedStoreId === store.id"
              [attr.aria-disabled]="store.disabled"
              tabindex="0"
            >
              <div
                cardBody
                (click)="onStoreChange(store.id)"
                role="region"
                [attr.aria-label]="'Store details'"
              >
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <div>
                      <app-csw-text
                        [text]="store | translation: 'name': lang | calioSafe: 'html'"
                        customClass="fw-600 fw-13"
                        role="heading"
                        aria-level="3"
                      />
                    </div>
                    @if (store.is_online === 0) {
                      <div
                        class="cbw-description whitespace-pre-line"
                        role="contentinfo"
                        [attr.aria-label]="'Store address'"
                      >
                        @let addressInput = {
                          street: store.street,
                          zip: store.zip,
                          city: store.city,
                          country: store.country | translation: 'name' : lang,
                          country_code: store.country?.identifier,
                        };
                        {{ addressInput | addressFormat: 'single' }}
                      </div>
                    }
                  </div>
                  <div class="next-arrow-container">
                    @if (selectedStoreId === store.id) {
                      <span
                        (click)="next()"
                        role="button"
                        [attr.aria-label]="'Continue with ' + (store | translation: 'name': lang)"
                        tabindex="0"
                      >
                        <img
                          [src]="baseUrl + '/images/next_icon_widget.svg'"
                          width="26px"
                          alt="Continue"
                        >
                      </span>
                    }
                  </div>
                </div>
              </div>
            </app-info-card>
          </div>
        }
      </div>

      @if (showStoreSelectionRequiredError) {
        <div
          class="cbsw-meeting-type-selection-setp-error cbsw-global-step-error"
          role="alert"
          aria-live="assertive"
          [attr.aria-invalid]="true"
        >
          <div class="text-danger">
            <div>{{ 'error_messages.select_one_option' | translate }}</div>
          </div>
        </div>
      }
    }
    <!-- Store Card View Ends -->
    <!-- Stores dropdown view starts -->
    @if (
      stores?.length > 0 &&
      enableStorePostcodeFeature !== 1 &&
      automaticStoreZipSearch !== 1 &&
      widgetService?.widgetConf?.partner?.show_stores_as_dropdown === 1
    ) {
      <div
        class="row cbsw-store-selection-step"
        role="region"
        [attr.aria-label]="'Store selection'"
      >
        <div class="col-md-12">
          <app-csw-text
            customClass="step-element-title fw-600"
            [text]="storeLabelTemplate | translation: 'template_content': lang | resolvePlaceholder: { store: bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore } | calioSafe: 'html'"
            role="heading"
            aria-level="2"
          />
        </div>
        <div class="col-md-12">
          <label for="store-select" class="visually-hidden">Select a store</label>
          <ng-select
            class="mt-3"
            id="store-select"
            name="store"
            placeholder=""
            [multiple]="false"
            [(ngModel)]="selectedStoreId"
            (change)="onStoreChange($event)"
            [required]="true"
            #storeControl="ngModel"
            [ngClass]="{ 'is-invalid': storeControl.touched && storeControl.invalid }"
            role="combobox"
            aria-haspopup="listbox"
            [attr.aria-expanded]="false"
            [attr.aria-required]="true"
            [attr.aria-invalid]="storeControl.touched && storeControl.invalid"
            [attr.aria-describedby]="'store-error-message'"
            tabindex="0"
          >
            @if (stores?.length > 1 && bookingService?.partnerData?.widget_show_all_stores_card === 1) {
              <ng-option
                [value]="0"
                role="option"
                [attr.aria-selected]="selectedStoreId === 0"
                [attr.aria-label]="(anyStoreLabelTemplate | translation: 'template_content': lang) + (selectedStoreId === 0 ? ' - Selected' : ' - Not selected')"
              >
                {{ anyStoreLabelTemplate | translation: 'template_content': lang }}
              </ng-option>
            }
            @for (store of stores; track store.id) {
              <ng-option
                [value]="store.id"
                [disabled]="store.disabled"
                role="option"
                [attr.aria-selected]="selectedStoreId === store.id"
                [attr.aria-disabled]="store.disabled"
                [attr.aria-label]="(store | translation: 'name': lang) + (selectedStoreId === store.id ? ' - Selected' : ' - Not selected')"
              >
                {{ store | translation: 'name': lang }}
              </ng-option>
            }
          </ng-select>
          @if (storeControl.touched && !storeControl.valid) {
            <div
              id="store-error-message"
              class="text-danger"
              role="alert"
              aria-live="assertive"
            >
              @if (storeControl?.errors?.required) {
                {{ 'error_messages.required_error_message' | translate }}
              }
            </div>
          }
        </div>
      </div>
    }
    <!-- Stores dropdown view ends -->
    <!-- Zip Code Stores dropdown view starts -->
    @if (
      (enableStorePostcodeFeature === 1 &&
      widgetService?.widgetConf?.partner?.show_stores_as_dropdown === 1) ||
      automaticStoreZipSearch === 1
    ) {
      <div
        class="row cbsw-store-selection-step"
        role="region"
        [attr.aria-label]="'Store selection by zip code'"
      >
        <div class="col-md-12">
          <app-csw-text
            customClass="step-element-title fw-600"
            [text]="storeLabelTemplate | translation: 'template_content': lang | resolvePlaceholder: { store: bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore } | calioSafe: 'html'"
            role="heading"
            aria-level="2"
          />
        </div>
        <div class="col-md-12">
          <label for="store-zip-select" class="visually-hidden">Search stores by zip code</label>
          <ng-select
            class="mt-3"
            [loading]="isStoreLoading"
            id="store-zip-select"
            name="store"
            #storeControl="ngModel"
            placeholder=""
            typeToSearchText="{{ 'booking_components.search_store_postcode_helper_text' | translate }}"
            notFoundText="{{ 'booking_components.store_not_found' | translate }}"
            [multiple]="false"
            [(ngModel)]="selectedStoreId"
            [required]="true"
            [ngClass]="{ 'is-invalid': storeControl.touched && storeControl.invalid }"
            [typeahead]="searchStores"
            role="combobox"
            aria-haspopup="listbox"
            [attr.aria-expanded]="false"
            [attr.aria-required]="true"
            [attr.aria-invalid]="storeControl.touched && storeControl.invalid"
            [attr.aria-describedby]="'store-zip-error-message'"
            [attr.aria-busy]="isStoreLoading"
            tabindex="0"
            (change)="onStoreChange($event)"
          >
            @for (store of stores; track $index) {
            <ng-option [value]="store.id" [disabled]="store.disabled">

              @if (bookingService?.partnerData?.include_city_name_in_postcode_results === 1 && store?.postcode_details) {
                <!--
                  case when wanted to show city name along with postcode and store name in bracket (e.g. EGK partner)
                  5600 Lenzburg (Solothurn)
                -->
                {{ store?.postcode_details?.postcode }} {{ store?.postcode_details?.city }}
                @if (store?.city) {
                  ({{ store.city }})
                }
              } @else {
                <!-- case when wanted to show only store name (e.g. BMS Sanitas) -->
                {{ store | translation: 'name': lang }}
              }
              @if (store.distance && widgetService?.widgetConf?.partner?.show_distance_in_stores === 1) {
              <span>
                ({{ 'booking_components.distance_from_store' | translate: {distance: (store.distance | number:'1.0-0')} }})
              </span>
              }
            </ng-option>
            }
            <ng-template ng-loadingspinner-tmp>
              <div class="lds-ellipsis">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </ng-template>
          </ng-select>
          <div>
            <small class="text-muted">
              {{ 'booking_components.search_store_by_postcode_placeholder' | translate }}
            </small>
          </div>
          @if (storeControl.touched && !storeControl.valid) {
            <div
              id="store-zip-error-message"
              class="text-danger"
              role="alert"
              aria-live="assertive"
            >
              @if (storeControl?.errors?.required) {
                {{ 'error_messages.required_error_message' | translate }}
              }
            </div>
          }
        </div>
      </div>
    }
    <!-- Zip Code Stores dropdown view ends -->
    <div
      class="d-flex justify-content-end mt-4 cbw-custom-navigation"
      role="navigation"
      [attr.aria-label]="'Store selection navigation'"
    >
      @if (showPreviousButton) {
        <div class="me-2">
          <app-button
            id="{{ 'store-selection-previous-btn' }}"
            (clickEvent)="previous()"
            [fontColor]="widgetService?.widgetConf?.button_font_color"
            [backgroundColor]="widgetService?.widgetConf?.button_background_color"
            role="button"
            [attr.aria-label]="((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PREVIOUS_BUTTON, type: 'template'}) | translation: 'template_content': lang) + ' - Go back to previous step'"
            tabindex="0"
          >
            <span
              class="custom-button-icon back"
              role="presentation"
              aria-hidden="true"
            ></span>
            {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_PREVIOUS_BUTTON, type: 'template'}) | translation: 'template_content': lang) }}
          </app-button>
        </div>
      }
      <div>
        <app-button
          id="{{ 'store-selection-next-btn' }}"
          (clickEvent)="next()"
          [fontColor]="widgetService?.widgetConf?.button_font_color"
          [backgroundColor]="widgetService?.widgetConf?.button_background_color"
          role="button"
          [attr.aria-label]="((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_NEXT_BUTTON, type: 'template' }) | translation: 'template_content': lang) + ' - Continue to next step'"
          [attr.aria-disabled]="!storeForm.valid"
          tabindex="0"
        >
          {{ ((bookingService.widgetTemplates | calioMeetingTemplate: { id: templateConstant.WIDGET_NEXT_BUTTON, type: 'template' }) | translation: 'template_content': lang) }}
          <span
            class="custom-button-icon next"
            role="presentation"
            aria-hidden="true"
          ></span>
        </app-button>
      </div>
    </div>
  </form>
}
